// Client-side state management for booru data using SWR pattern
import useSWR from "swr"
import type { BooruPost, BooruSearchParams, BooruSite } from "./booru-api"
import { fetchBooruClient } from "./booru-client"
import { useSourceStore } from "./source-store"
import { useBooruCredentialsStore } from "./booru-credentials-store"
import { screenPosts } from "./platform-screen"
import { withE621Order } from "./e621-filter-terms"

// SWR fetcher for booru searches
const booruFetcher = async ([sites, params]: [BooruSite[], BooruSearchParams]) => {
  const store = useSourceStore.getState()
  const settingsStore = await import('./settings-store').then(m => m.useSettingsStore.getState())
  const searchMode = settingsStore.searchMode
  const phraseToTags = settingsStore.phraseToTags

  // Separate sources by fetch mode
  const clientSources = sites.filter((site) => {
    const config = store.getSource(site)
    const isClient = config?.fetchMode === "client"
    console.log(`[v0] ${site}: fetchMode=${config?.fetchMode}, isClient=${isClient}`)
    return isClient
  })

  const serverSources = sites.filter((site) => {
    const config = store.getSource(site)
    return config?.fetchMode !== "client"
  })

  console.log(`[v0] Client sources:`, clientSources)
  console.log(`[v0] Server sources:`, serverSources)
  console.log(`[v0] Search mode:`, searchMode)

  const allPosts: BooruPost[] = []

  // Fetch client-side sources directly from browser
  if (clientSources.length > 0) {
    console.log("[v0] Fetching client-side sources:", clientSources)
    const clientPromises = clientSources.map((site) => {
      console.log(`[v0] Starting client-side fetch for ${site}`)
      return fetchBooruClient(site, {
        tags: withE621Order(site, params.tags?.join(",") || "", params.e621Order),
        negativeTags: params.negativeTags || [],
        limit: params.limit || 20,
        page: params.page || 0,
      })
    })
    // Platform tags: browser-fetched posts never pass the search route, so the server screens them here (lib/platform-screen.ts).
    const clientResults = await Promise.all(clientPromises.map((p) => p.then((posts) => screenPosts(posts))))
    console.log(`[v0] Client-side results:`, clientResults.map(r => r.length))
    allPosts.push(...clientResults.flat())
  }

  // Fetch server-side sources through API route
  if (serverSources.length > 0) {
    console.log("[v0] Fetching server-side sources:", serverSources)
    
    // Get credentials from localStorage for server-side sources
    const credentialsStore = useBooruCredentialsStore.getState()
    const userCredentials: Record<string, { username?: string; apiKey?: string }> = {}
    
    for (const site of serverSources) {
      const creds = credentialsStore.getCredentials(site)
      if (creds) {
        userCredentials[site] = creds
        console.log(`[v0] Including credentials for ${site} in server request`)
      }
    }
    
    // Include custom sources, and any source with a live board/category selection
    // (e.g. erome's version filter, 4chan's board picks) so the server uses the
    // user's current choice instead of falling back to its own default config.
    const customSourceConfigs = serverSources
      .map((site) => store.getSource(site))
      .filter((cfg): cfg is NonNullable<typeof cfg> => !!cfg && (!!cfg.isCustom || !!cfg.boards))

    const response =
      customSourceConfigs.length > 0
        ? await fetch("/api/booru/search", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sites: serverSources,
              tags: params.tags || [],
              negativeTags: params.negativeTags || [],
              limit: params.limit || 20,
              page: params.page || 0,
              rating: params.rating || "all",
              sourceConfigs: customSourceConfigs,
              userCredentials, // Pass credentials from localStorage
              searchMode, // Pass search mode
              phraseToTags,
              audioChanSort: params.audioChanSort,
              civitaiMediaType: params.civitaiMediaType,
              // These two were previously missing from this body entirely —
              // the tabs that set them (HentaieraCategoryTabs, LewdvaultSortTabs)
              // updated client state, but the value never actually reached the
              // server-side search, so picking a category/sort had no effect.
              hentaieraCategory: params.hentaieraCategory,
              hitomiFilter: params.hitomiFilter,
              goontaiGenre: params.goontaiGenre,
              lewdvaultSort: params.lewdvaultSort,
              doujinsCategory: params.doujinsCategory,
              e621Order: params.e621Order,
              yandereOrder: params.yandereOrder,
              sourceOptions: params.sourceOptions,
              sortBy: params.sortBy,
              sortDirection: params.sortDirection,
            }),
          })
        : await fetch("/api/booru/search", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sites: serverSources,
              tags: params.tags || [],
              negativeTags: params.negativeTags || [],
              limit: params.limit || 20,
              page: params.page || 0,
              rating: params.rating || "all",
              userCredentials, // Pass credentials from localStorage
              searchMode, // Pass search mode
              phraseToTags,
              audioChanSort: params.audioChanSort,
              civitaiMediaType: params.civitaiMediaType,
              hentaieraCategory: params.hentaieraCategory,
              hitomiFilter: params.hitomiFilter,
              goontaiGenre: params.goontaiGenre,
              lewdvaultSort: params.lewdvaultSort,
              doujinsCategory: params.doujinsCategory,
              e621Order: params.e621Order,
              yandereOrder: params.yandereOrder,
              sourceOptions: params.sourceOptions,
              sortBy: params.sortBy,
              sortDirection: params.sortDirection,
            }),
          })
// Tolerate a non-JSON body (an HTML error page from a proxy, say) so the
    // thrown error below is the useful one rather than a parse failure.
    const data = await response.json().catch(() => null)

    // A failed search must surface as a thrown error, NOT as an empty result
    // set. This used to silently drop the response whenever `success` was
    // falsy (and never checked `response.ok` at all), so a 500 from
    // /api/booru/search became an ordinary "0 posts" success as far as SWR
    // was concerned — which SWR then CACHED against this exact key. Toggling
    // the source off and back on rebuilds the identical cache key, so the
    // cached false-empty got served straight back and the grid stayed stuck
    // on "No posts found / Source May Be Unavailable" with no retry. That's
    // the "sometimes it doesn't load sources even when I turn it on and off"
    // report. Throwing instead lets SWR treat it as a real error and run its
    // configured retries, and keeps the bogus empty out of the cache.
    if (!response.ok || !data?.success) {
      const detail = data?.error || `HTTP ${response.status}`
      throw new Error(`Booru search failed for ${serverSources.join(", ")}: ${detail}`)
    }

    allPosts.push(...(data.posts || []))
  }

  return allPosts
}

export function useBooruSearch(sites: BooruSite[], params: BooruSearchParams) {
  // Create a stable cache key by stringifying params to ensure SWR detects changes
  const key = sites.length > 0 ? JSON.stringify([sites, params]) : null
  console.log('[useBooruSearch] Cache key:', key)
  console.log('[useBooruSearch] Params:', params)
  
  const { data, error, isLoading, mutate } = useSWR<BooruPost[]>(key, 
    async (key) => {
      console.log('[useBooruSearch] Fetching with key:', key)
      const [sites, params] = JSON.parse(key)
      const result = await booruFetcher([sites, params])
      console.log('[useBooruSearch] Fetched result:', {
        count: result.length,
        page: params.page,
        limit: params.limit
      })
      return result
    }, 
    {
      revalidateOnFocus: false,
      dedupingInterval: 1000, // Prevent duplicate requests within 1 second
      keepPreviousData: true,
      errorRetryCount: 2,
      errorRetryInterval: 1000,
    }
  )

  if (data && data.length > 0) {
    console.log("[useBooruSearch] Returning posts:", {
      count: data.length,
      page: params.page,
      firstPost: {
        id: data[0].id,
        site: data[0].site,
        previewUrl: data[0].previewUrl,
        fileUrl: data[0].fileUrl,
      },
    })
  }

  // hasMore logic: we have more if we got ANY posts back
  // Sources have different page sizes (e.g. XlecX returns 18, not the requested 25)
  // so we can't compare against requestedLimit. Empty response = no more pages.
  const requestedLimit = params.limit || 20
  const hasMore = data ? data.length > 0 : true

  console.log(`[useBooruSearch] hasMore calculation:`, {
    gotPosts: data?.length || 0,
    requestedLimit,
    hasMore,
    currentPage: params.page,
    isLoading
  })

  return {
    posts: data || [],
    loading: isLoading, // Added for backwards compatibility
    error: error,
    hasMore: hasMore,
    refresh: mutate,
  }
}

// Tag suggestions and autocomplete
export function useTagSuggestions(site: BooruSite, query: string) {
  const { data, isLoading } = useSWR(
    query.length > 2 ? ["tags", site, query] : null,
    async () => {
      const response = await fetch(`/api/booru/tags?site=${site}&q=${encodeURIComponent(query)}&limit=10`)
      const data = await response.json()
      return data.suggestions || []
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    },
  )

  return {
    suggestions: data || [],
    isLoading,
  }
}
